import React, { useState } from "react";
import {
  doc,
  updateDoc
} from "firebase/firestore";
import { db } from "../../firebase";
import { useFamilyData } from "../../hooks/useFamilyData";
import { callJoinFamilyByCode, needsKidReplace } from "../../utils/joinFamilyByCode";
import { useToast } from "../../components/ui/ToastProvider";
import { useConfirm } from "../../components/ui/ConfirmProvider";
import { mapFirebaseError } from "../../utils/userErrors";

const INVITE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const generateInviteCode = (length = 6) => {
  if (typeof window !== "undefined" && window.crypto?.getRandomValues) {
    const values = new Uint32Array(length);
    window.crypto.getRandomValues(values);
    return Array.from(values, (v) => INVITE_CHARS[v % INVITE_CHARS.length]).join("");
  }
  return Array.from({ length }, () => INVITE_CHARS[Math.floor(Math.random() * INVITE_CHARS.length)]).join("");
};

export default function FamilyCard({ familyId, familySettings, canManage }) {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [joinCode, setJoinCode] = useState("");
  const [joinName, setJoinName] = useState("");
  const [joinError, setJoinError] = useState("");
  const { members = [] } = useFamilyData(familyId, { canManage });
  const isPro = Boolean(familySettings?.isPro);

  const generateParentInvite = async () => {
    if (!canManage) return;
    await updateDoc(doc(db, "families", familyId), {
      parentInviteCode: generateInviteCode()
    });
  };

  const generateKidInvite = async () => {
    if (!canManage) return;
    await updateDoc(doc(db, "families", familyId), {
      kidInviteCode: generateInviteCode()
    });
  };

  const joinFamily = async () => {
    if (!canManage) return;
    if (!joinName.trim()) {
      setJoinError("Username is required.");
      return;
    }
    if (!joinCode.trim()) {
      setJoinError("Invite code is required.");
      return;
    }
    setJoinError("");
    try {
      await callJoinFamilyByCode({ code: joinCode, name: joinName.trim() });
      showToast({ type: "success", message: "Joined family." });
      setJoinName("");
      setJoinCode("");
    } catch (error) {
      if (needsKidReplace(error)) {
        const proceed = await confirm({
          title: "Replace existing kid login?",
          message:
            "A kid with this name already exists. Joining will replace their login and keep their data.",
          confirmLabel: "Replace and join",
          cancelLabel: "Cancel",
          danger: true
        });
        if (!proceed) return;
        await callJoinFamilyByCode({ code: joinCode, name: joinName.trim(), forceReplace: true });
        showToast({ type: "success", message: "Joined family." });
        setJoinName("");
        setJoinCode("");
        return;
      }
      setJoinError(mapFirebaseError(error, "Unable to join family."));
    }
  };

  return (
    <div className="glass parent-card">
      <div className="header-flex">
        <h2>Family & Guardians</h2>
      </div>

      <div className="card-body">
        {!canManage && (
          <p style={{ textAlign: "center", opacity: 0.6 }}>You have view-only access.</p>
        )}
        {!isPro && (
          <p style={{ fontSize: "0.8rem", opacity: 0.75 }}>
            Parent and kid invite accounts are a Pro feature. Free families stay on one account.
          </p>
        )}
        <div style={{ display: "flex", gap: "8px" }}>
          <button className="btn-nav" onClick={generateParentInvite} disabled={!isPro || !canManage}>
            Parent Invite
          </button>
          <button className="btn-nav" onClick={generateKidInvite} disabled={!isPro || !canManage}>
            Kid Invite
          </button>
        </div>

        {familySettings.parentInviteCode && (
          <p>
            Parent Code:{" "}
            <strong>{familySettings.parentInviteCode}</strong>
          </p>
        )}
        {familySettings.kidInviteCode && (
          <p>
            Kid Code:{" "}
            <strong>{familySettings.kidInviteCode}</strong>
          </p>
        )}

        <input
          placeholder="Username"
          value={joinName}
          onChange={e => setJoinName(e.target.value)}
          disabled={!isPro || !canManage}
        />
        <input
          placeholder="Invite code"
          value={joinCode}
          onChange={e => setJoinCode(e.target.value)}
          disabled={!isPro || !canManage}
        />

        <button className="btn-nav" onClick={joinFamily} disabled={!isPro || !canManage}>
          Join Family
        </button>
        {joinError && (
          <small className="pin-error" role="alert">
            {joinError}
          </small>
        )}

        {(familySettings.parentInviteCode || familySettings.kidInviteCode) && (
          <p style={{ fontSize: "0.75rem", opacity: 0.7 }}>
            Share the parent or kid code based on the account you want to add.
          </p>
        )}

        {members.length > 0 && (
          <div style={{ marginTop: "12px" }}>
            <h3 style={{ marginBottom: "6px" }}>Roles</h3>
            {members.map(member => (
              <div key={member.id} className="parent-list-item" style={{ gap: "8px" }}>
                <span>{member.displayName || "Unnamed member"}</span>
                <select
                  value={member.role === "viewer" ? "kid" : (member.role || "kid")}
                  onChange={(e) => {
                    if (!canManage || !isPro) return;
                    updateDoc(doc(db, "families", familyId, "members", member.id), { role: e.target.value });
                  }}
                  disabled={!canManage || !isPro}
                >
                  <option value="parent">Parent</option>
                  <option value="kid">Kid</option>
                </select>
              </div>
            ))}
            <p style={{ fontSize: "0.75rem", opacity: 0.7 }}>
              Kid accounts can view the family and mark chores done for approval.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
